import Phaser from 'phaser';
import { PlayerController } from './PlayerController';
import { AudioManager } from '../Systems/AudioManager';

export class MovingPlatform extends Phaser.Physics.Arcade.Sprite {
  private startX: number;
  private startY: number;
  private targetX: number;
  private targetY: number;
  private speed: number;
  private movingToTarget = true;

  constructor(scene: Phaser.Scene, x: number, y: number, targetX: number, targetY: number, speed = 100) {
    super(scene, x, y, 'platform_moving');
    this.startX = x;
    this.startY = y;
    this.targetX = targetX;
    this.targetY = targetY;
    this.speed = speed;

    scene.add.existing(this);
    scene.physics.add.existing(this, true); // Static body for platform collision
  }

  public updatePlatform(delta: number): void {
    const destX = this.movingToTarget ? this.targetX : this.startX;
    const destY = this.movingToTarget ? this.targetY : this.startY;

    const angle = Phaser.Math.Angle.Between(this.x, this.y, destX, destY);
    const dist = Phaser.Math.Distance.Between(this.x, this.y, destX, destY);

    if (dist < 4) {
      this.movingToTarget = !this.movingToTarget;
    } else {
      const step = (this.speed * delta) / 1000;
      this.x += Math.cos(angle) * step;
      this.y += Math.sin(angle) * step;
      // Refresh static body position for Arcade Physics
      if (this.body) {
        (this.body as Phaser.Physics.Arcade.StaticBody).updateFromGameObject();
      }
    }
  }
}

export class BounceFlower extends Phaser.Physics.Arcade.Sprite {
  public boostPower: number;

  constructor(scene: Phaser.Scene, x: number, y: number, boostPower = 680) {
    super(scene, x, y, 'bounce_flower');
    this.boostPower = boostPower;

    scene.add.existing(this);
    scene.physics.add.existing(this, true);
  }

  public triggerBounce(player: PlayerController): void {
    player.bounceUpward(-this.boostPower);
    AudioManager.playSFX('jump');

    // Bounce animation
    this.scene.tweens.add({
      targets: this,
      scaleY: 1.4,
      duration: 100,
      yoyo: true,
    });
  }
}

export class PressureSwitch extends Phaser.Physics.Arcade.Sprite {
  public switchId: string;
  public doorId: string;
  public isPressed = false;

  constructor(scene: Phaser.Scene, x: number, y: number, id: string, doorId: string) {
    super(scene, x, y, 'switch_off');
    this.switchId = id;
    this.doorId = doorId;

    scene.add.existing(this);
    scene.physics.add.existing(this, true);
  }

  public press(onDoorTrigger: (doorId: string) => void): void {
    if (this.isPressed) return;
    this.isPressed = true;
    this.setTexture('switch_on');
    AudioManager.playSFX('door');
    onDoorTrigger(this.doorId);
  }
}

export class MechanicalDoor extends Phaser.Physics.Arcade.Sprite {
  public doorId: string;
  public isOpen = false;

  constructor(scene: Phaser.Scene, x: number, y: number, id: string) {
    super(scene, x, y, 'door_closed');
    this.doorId = id;

    scene.add.existing(this);
    scene.physics.add.existing(this, true);
  }

  public openDoor(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // Slide upward animation
    this.scene.tweens.add({
      targets: this,
      y: this.y - 80,
      alpha: 0.3,
      duration: 600,
      onComplete: () => {
        if (this.body) {
          (this.body as Phaser.Physics.Arcade.StaticBody).enable = false;
        }
      },
    });
  }
}

export class BreakableBlock extends Phaser.Physics.Arcade.Sprite {
  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'tile_breakable');

    scene.add.existing(this);
    scene.physics.add.existing(this, true);
  }

  public breakBlock(): void {
    AudioManager.playSFX('enemy_hit');
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: 0,
      scaleY: 0,
      duration: 150,
      onComplete: () => {
        this.destroy();
      },
    });
  }
}
